/**
 * Contratos de Datos Canónicos — Hub Comercial, Sucursales, Contactos y Proyectos de Tienda (Odoo 18)
 * Archivo: src/types/crm.ts
 * 
 * PLAN-050 & PLAN-050-R — Fase 1 Endurecida del Blueprint Arquitectónico Odoo 18:
 * Define los tipos y opciones verificadas contra Notion ERP para:
 * - BD_Clientes (31e86805-4e27-8060-a52b-c2f81d58f466)
 * - BD_Sucursales (35486805-4e27-8037-b71a-cdf8deffd385)
 * - BD_Contactos (bc77f995-7265-4861-a0c2-8bc5390b9286)
 * - BD_Proyectos (31e86805-4e27-80e0-8be5-f3d30532e900)
 * - BD_Pedidos (3d086805-4e27-814b-9ff4-e694d56a58bb)
 */

export const CLIENT_TYPES = [
  'Corporativo / Empresa',
  'Persona Natural / Diseñador'
] as const;

export const CLIENT_STATUSES = [
  'Activo',
  'Inactivo',
  'Prospecto / Lead'
] as const;

export const BRANCH_TYPES = [
  'Franquicia',
  'Tienda Propia',
  'Distribuidor Autorizado',
  'Almacén Central'
] as const;

export const BRANCH_STATUSES = [
  'Operativa',
  'En Remodelación',
  'Próxima Apertura',
  'Cerrada'
] as const;

export const WALL_SUBSTRATES = [
  'Mampostería / Concreto',
  'Drywall Estándar',
  'Drywall Reforzado',
  'Estructura Metálica',
  'Por Definir / Mixta'
] as const;

export const FLOOR_SUBSTRATES = [
  'Porcelanato / Cerámica',
  'Concreto Pulido',
  'Mezzanina Metálica'
] as const;

export const PROJECT_STATES = [
  'Cotización',
  'En producción',
  'En despacho',
  'Instalado',
  'Cerrado'
] as const;

export interface CRMOrderSummary {
  id: string;
  codigo: string;
  clienteId: string | null;
  proyectoId: string | null;
  presupuestoIds?: string[];
  tipo: 'PED' | 'PRS' | 'FAC' | 'OE';
  estado: string;
  estadoAuditoriaBOM: string;
  fecha: string;
  montoUSD: number;
}

export interface CRMBudgetSummary {
  id: string;
  codigo: string;
  correlativoValery?: string;
  clienteId: string | null;
  proyectoId: string | null;
  proyectoIds: string[];
  pedidoIds: string[];
  estadoComercial: string;
  modalidadKit: string;
  fechaEmision: string;
  subtotalUSD: number;
  ivaUSD: number;
  totalUSD: number;
}

export interface CRMContact {
  id: string;
  nombre: string;
  cedula: string;
  telefono: string;
  email: string;
  roles: string[];
  propietarioDe: string[];
  activo: boolean;
  createdTime?: string;
}

export interface CRMClient {
  id: string;
  nombre: string;
  razonSocial: string;
  rif: string;
  codigoValery: string;
  tipoCliente: string;
  estatus: string;
  contactoPrincipal: string;
  telefono: string;
  correo: string;
  direccionFiscal: string;
  ciudadEstado: string;
  contribuyenteEspecial: boolean;
  diasCredito: number;
  limiteCreditoUSD: number;
  totalCotizadoUSD: number;
  sucursalIds: string[];
  proyectoIds: string[];
  pedidoIds: string[];
  sucursalesCount?: number;
  proyectosCount?: number;
  pedidosCount?: number;
  createdTime?: string;
}

export interface CRMBranch {
  id: string;
  nombre: string;
  codigoSucursal: string;
  clienteId: string | null;
  clienteIds: string[];
  clienteNombre?: string;
  clienteRif?: string;
  proyectoIds: string[];
  proyectosCount?: number;
  tipoSucursal: string;
  estatus: string;
  ciudadEstado: string;
  centroComercial: string;
  direccion: string;
  contactoTienda: string;
  telefonoSucursal: string;
  sustratoPared: string;
  sustratoPiso: string;
  distribucionDrywallPct: number;
  createdTime?: string;
}

export interface CRMProject {
  id: string;
  nombre: string;
  estado: string;
  clienteId: string | null;
  clienteIds: string[];
  clienteNombre?: string;
  clienteRif?: string;
  sucursalId: string | null;
  sucursalIds: string[];
  sucursalNombre?: string;
  ciudadEstado?: string;
  centroComercial?: string;
  sustratoPared?: string;
  sustratoPiso?: string;
  distribucionDrywallPct?: number;
  pedidoIds: string[];
  orders?: CRMOrderSummary[];
  ordersCount?: number;
  budgets?: CRMBudgetSummary[];
  budgetsCount?: number;
  fechaInicio: string;
  fechaCierre: string;
  montoTotalUSD: number;
  montoConfirmadoPedidosUSD?: number;
  montoCotizadoPresupuestosUSD?: number;
  notas: string;
  createdTime?: string;
}

export interface CRMOverviewResponse {
  clients: CRMClient[];
  branches: CRMBranch[];
  projects: CRMProject[];
  orders: CRMOrderSummary[];
  budgets?: CRMBudgetSummary[];
  contacts?: CRMContact[];
  kpis: {
    totalClients: number;
    activeClients: number;
    totalBranches: number;
    totalProjects: number;
    activeProjects: number;
    totalOrders: number;
    totalBudgets?: number;
    totalContacts?: number;
  };
  source?: string;
  snapshotAt?: string | null;
  timestamp?: string;
  error?: string;
}

// =============================================================================
// PLAN-051 — Catálogo Maestro de Mobiliario PT y Recetas BOM (Odoo 18 mrp.bom)
// =============================================================================

export const PT_FAMILIES = [
  'Herrería',
  'Carpintería',
  'General / Accesorios',
  'Mobiliario Especial',
  'Mobiliario Comercial',
  'Acrílico y Vidrio',
  'Pintura y Acabados',
  'Exhibidores'
] as const;

export const PT_UNITS = [
  'UND',
  'UNID',
  'JUEGO',
  'METROS',
  'ML',
  'MT2',
  'GL',
  'SERV'
] as const;

export const PT_STATUSES = [
  'Activo',
  'En Desuso',
  'Prototipo',
  'En revisión'
] as const;

export const PT_ORIGINS = [
  'Valery ERP (Oficial)',
  'Taller / Sanesca STD',
  'Valery ERP',
  'Drive 2026 (Cuarentena)'
] as const;

export const PT_FIXATION_LOADS = [
  '⚪ Sin Fijación / Directo',
  '🔵 Autosoportante Piso',
  '🟠 Anclaje Estándar',
  '🔴 Anclaje Pesado'
] as const;

export const BOM_PRODUCTION_STAGES = [
  'Carpintería / Estructura',
  'Revestimiento / Formica',
  'Pintura y Acabado',
  'Electricidad e Iluminación',
  'Ensamble / Herrajes'
] as const;

export interface BOMMaterialRef {
  code: string;        // Ej: "M66" o SKU directo "QUI-004"
  sku: string;         // Ej: "TUB-021"
  notionId: string | null;
  nombre: string;      // Ej: "Tubo Hierro 1X1 1.1Mm E"
  unidad: string;      // Ej: "Tubo / Barra"
  costoUSD: number;    // Ej: 2.0
  stockBase?: number;  // Existencia física en BD_Catalogo_Insumos
  categoria?: string;
}

export interface ManufacturingOperationRef {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  estatus: string;
}

export interface BOMRecipeLine {
  id?: string;
  matCode: string;     // Ej: "M66" o SKU directo
  sku?: string;
  insumoNotionId?: string | null;
  nombre: string;
  unidad: string;
  cantidadBase: number;
  mermaPct: number;
  etapaProductiva: string;
  costoUnitarioUSD: number;
  subtotalUSD: number;
  stockBase?: number;
  notas?: string;
  source?: 'valery_index' | 'notion_bom' | 'kv_custom';
}

export interface PTFurnitureItem {
  id: string;
  codigo: string;               // Ej: "P862", "PROD-VIGA-004"
  nombre: string;               // Ej: "CREMALLERA 240CM TUBO 1\" CON ENGANCHE IZQ ADIDAS"
  familia: string;              // Ej: "Herrería"
  unidadMedida: string;         // Ej: "UND"
  unidad?: string;              // Alias de unidadMedida ("UND", "ML", "MT2", "SERV")
  isParametricByMeasure?: boolean;
  estatus: string;              // Ej: "Activo"
  origenRegistro: string;       // Ej: "Valery ERP (Oficial)"
  esKitModular: boolean;
  isTrueCompositeKit?: boolean; // True si es un Juego/Set compuesto real o posee SubKit 4-UP
  subKitEnsamble: string;       // Desglose 4-UP (cada línea <= 80 chars)
  suggestedSubKit4UP?: string;  // Desglose 4-UP autoderivado determinísticamente (<= 80 chars)
  requiereFijacion: boolean;
  tipoCargaFijacion: string;
  puntosPared: number;
  puntosPiso: number;
  empaqueNominal: number;
  pedidosCount: number;
  operacionIds?: string[];
  operacionesNombres?: string[];
  materialIds?: string[];
  hasBOM: boolean;
  bomSource: 'valery_index' | 'notion_bom' | 'kv_custom' | 'hybrid' | 'none';
  insumosCount: number;
  costoTeoricoUSD: number;
  bomLines: BOMRecipeLine[];
  createdTime?: string;
}

export interface PTCatalogOverviewResponse {
  items: PTFurnitureItem[];
  materialsCatalog: BOMMaterialRef[];
  operationsCatalog?: ManufacturingOperationRef[];
  kpis: {
    totalPT: number;
    activePT: number;
    withBOMCount: number;
    withoutBOMCount: number;
    kitsModularesCount: number;
    compositeKitsCount?: number;
    requiereFijacionCount: number;
    totalMaterialsRef: number;
    linkedNotionInsumosCount?: number;
    totalOperationsRef?: number;
  };
  source?: string;
  snapshotAt?: string | null;
  timestamp?: string;
  error?: string;
}

// =============================================================================
// PLAN-052 — Gestión e Ingesta Híbrida 360° de Pedidos y Líneas 4-UP (Odoo 18 sale.order)
// =============================================================================

export const ORDER_DOC_TYPES = [
  'Pedido (PED)',
  'Presupuesto (PRS)',
  'Orden de Entrega (O/E)',
  'Factura (FAC)'
] as const;

export const ORDER_STATUSES = [
  'Borrador',
  'Enviado',
  'Confirmado',
  'En producción',
  'Despachado',
  'Cerrado'
] as const;

export const ORDER_LINE_STATUSES = [
  'Sin producir',
  'En producción',
  'Producido',
  'Embalado',
  'Parcial',
  'Despachado'
] as const;

export const ORDER_DISPATCH_STATUSES = [
  'Pendiente',
  'En Rampa',
  'Parcial',
  'Despachado'
] as const;

export const ORDER_LINE_TYPES = [
  'PT_FABRICADO',
  'PT_PARAMETRICO',
  'SUMINISTRO_FORANEO',
  'SERVICIO_INTANGIBLE'
] as const;
export type OrderLineType = typeof ORDER_LINE_TYPES[number];

export const LOGISTICS_ROUTE_TYPES = [
  'VIA_GALPON',
  'DIRECTO_OBRA',
  'NO_APLICA'
] as const;
export type LogisticsRouteType = typeof LOGISTICS_ROUTE_TYPES[number];

export const SERVICE_EXECUTION_STATES = [
  'No Aplica',
  'Pendiente',
  'En Ejecución',
  'Ejecutado en Obra',
  'PENDIENTE',
  'CONTRATADO',
  'EN_EJECUCION_SITIO',
  'EJECUTADO_CONFORME'
] as const;
export type ServiceExecutionState = typeof SERVICE_EXECUTION_STATES[number];

export interface OverrideBomLine {
  matCode: string;
  sku?: string;
  insumoNotionId?: string | null;
  nombre: string;
  unidad: string;
  cantidadTotalObra: number;
  cantidadPorUnidad?: number;
  mermaPct?: number;
  etapaProductiva?: string;
  costoUnitarioUSD?: number;
  notas?: string;
}

export interface OrderLine360 {
  id: string;
  orderId: string;
  orderCodigo: string;
  lineTitle: string;
  code: string;               // Código base PT (ej: "P484", "P1257", "SERV-FLETE")
  codeEtiqueta: string;       // Código en etiqueta 4-UP con sufijo/variante (ej: "P484 [1/2]", "P1294-IZQ")
  desc: string;               // Descripción técnica completa
  descEtiqueta: string;       // Rótulo térmico 4-UP (<= 80 caracteres)
  labelLength: number;        // Longitud en caracteres de descEtiqueta
  labelValid80: boolean;      // True si descEtiqueta.length <= 80
  cantidad: number;
  unidadMedida?: string;      // "UND" | "JUEGO" | "METROS" | "MT2" | "GL"
  color: string;              // En mayúsculas (ej: "NEGRO", "BLANCO")
  precioUnitarioUSD: number;
  subtotalPrecioUSD: number;
  estadoProduccion: string;   // "Sin producir" | "En producción" | "Producido" | "Embalado" | "Parcial" | "Despachado"
  estadoDespacho: string;     // "Pendiente" | "En Rampa" | "Parcial" | "Despachado"
  tipoLinea?: OrderLineType;
  rutaLogistica?: LogisticsRouteType;
  estadoServicioObra?: ServiceExecutionState;
  requiereEmpaqueGalpon?: boolean;
  bultosDeclarados?: number;
  bultosCalculados4UP?: number;
  overrideBomLines?: OverrideBomLine[];
  operacionIds?: string[];
  ptNotionId: string | null;  // Vínculo a BD_Catalogo_Mobiliario_PT
  familia: string;
  empaqueNominal: number;
  esKitCompuesto: boolean;
  hasBOM: boolean;
  costoUnitarioBOMUSD: number;
  costoTotalBOMUSD: number;
  requiereFijacion: boolean;
  puntosPared: number;
  puntosPiso: number;
}

export interface Order360Detail {
  id: string;
  codigo: string;             // Ej: "PED-TALIB-P110", "P0040"
  correlativoValery: string;
  tipoDocumento: string;      // "Pedido (PED)" | "Presupuesto (PRS)" | "Orden de Entrega (O/E)" | "Factura (FAC)"
  tipoCorto: 'PED' | 'PRS' | 'OE' | 'FAC';
  estado: string;             // "Borrador" | "Enviado" | "Confirmado" | "En producción" | "Despachado" | "Cerrado"
  estadoAuditoriaBOM: string; // "Sin Auditar" | "Corte Preliminar" | "Auditado Conforme" | "Con Desviación"
  moneda: string;             // "USD" | "VES"
  fechaDocumento: string;
  fechaEntregaEstimada: string;
  clienteId: string | null;
  clienteNombre: string;
  clienteRif: string;
  proyectoId: string | null;
  proyectoNombre: string;
  sucursalId: string | null;
  sucursalNombre: string;
  ciudadEstado: string;
  sustratoPared: string;
  sustratoPiso: string;
  direccionEntrega: string;
  contactoEntrega: string;
  notas: string;
  montoTotalUSD: number;
  costoTeoricoTotalBOMUSD: number;
  linesCount: number;
  unidadesTotales: number;
  bultosEstimados: number;
  inRampaActivos: boolean;    // True si también está activo en data/pedidos_activos.js
  allLabelsValid80: boolean;
  lines: OrderLine360[];
}

export interface Orders360CatalogResponse {
  orders: Order360Detail[];
  kpis: {
    totalOrders: number;
    ordersWithLines: number;
    totalLines: number;
    totalUnits: number;
    activeProductionOrders: number;
    inRampaCount: number;
    linkedToPTLinesCount: number;
    protectedLabels80Pct: number;
    totalBOMTheoreticalUSD: number;
  };
  source?: string;
  snapshotAt?: string | null;
  timestamp?: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// PLAN-053: Planificador de Explosión MRP Ex-Ante en 3 Capas (mrp.production)
// ---------------------------------------------------------------------------

export type MRPExplosionLayer = 'FABRICACION_BOM' | 'EMBALAJE_4UP' | 'FIJACION_OBRA';

export interface MRPSubcontractingServiceLine {
  lineId: string;
  orderId: string;
  orderCodigo: string;
  code: string;
  codeEtiqueta: string;
  desc: string;
  descEtiqueta: string;
  cantidad: number;
  unidadMedida: string;
  tipoLinea: 'SERVICIO_INTANGIBLE' | 'SUMINISTRO_FORANEO';
  rutaLogistica: LogisticsRouteType;
  estadoServicioObra: ServiceExecutionState;
  precioUnitarioUSD: number;
  subtotalPrecioUSD: number;
}

export interface MRPExplodedMaterialLine {
  key: string;                  // Identificador único (sku o matCode)
  matCode: string;              // Ej: "M66" o "TOR-003"
  sku: string;                  // Ej: "TUB-021", "TOR-003"
  insumoNotionId: string | null;// UUID en BD_Catalogo_Insumos (26286805-4e27-8067-8847-d39de1bf0bde)
  nombre: string;               // Nombre operativo / ISO del insumo
  categoria: string;            // Ej: "Perfiles y Tubos", "Tornillería y Fijación", "Embalaje"
  unidadConsumoBOM?: string;    // Nivel 1: Unidad de consumo en la receta BOM (ej: "Litro", "ML")
  demandaConsumoBOM?: number;   // Nivel 1: Cantidad consumida en la unidad de receta BOM
  unidad: string;               // Nivel 2: Unidad física de stock en almacén (ej: "Galón", "Lámina")
  uomCompra?: string;           // Nivel 3: Presentación comercial de compra OAB (ej: "Barra 6m (6 ML)", "Galón (3.785 L)")
  factorUoM?: number;
  capa: MRPExplosionLayer;      // Capa 1 (Fabricación BOM), Capa 2 (Embalaje 4-UP), Capa 3 (Fijación Sustrato)
  capaLabel: string;
  cantidadTeoricaBruta: number; // Nivel 2: Demanda matemática total en unidad de almacén
  yaReservadoObra: number;      // Ya apartado en live_stock_allocations para este pedido/proyecto
  demandaNeta: number;          // max(0, cantidadTeoricaBruta - yaReservadoObra)
  stockFisico: number;          // Stock físico total en BD_Catalogo_Insumos / inventory.json
  apartadoOtrasObras: number;   // Stock reservado para otros proyectos activos
  stockLibre: number;           // max(0, stockFisico - apartadoOtrasObras - yaReservadoObra)
  sugeridoReservarMTO: number;  // min(demandaNeta, stockLibre)
  deficitComprarOAB: number;    // max(0, demandaNeta - sugeridoReservarMTO)
  cantidadComercialCompraOAB?: number; // Nivel 3: Cantidad comercial redondeada (Math.ceil) para orden de compra
  costoCompraUSD?: number;      // Nivel 3: Desembolso comercial real por presentaciones completas
  remanenteStockCompra?: number;// Sobrante que ingresará a Stock Libre tras la compra comercial
  costoUnitarioUSD: number;
  costoTotalRequeridoUSD: number;
  costoDeficitOABUSD: number;
  estadoCobertura: 'CUBIERTO_STOCK' | 'PARCIAL' | 'DEFICIT_TOTAL' | 'YA_RESERVADO';
  esEstimacionParametrica?: boolean;
  warningRatioReceta?: string | null;
  origenMuebles: string[];      // Ej: ["P822 (x1)", "P821 (x1)"]
  origenLineIds?: string[];
}

export interface MRPOrderExplosionResponse {
  scopeType: 'order' | 'project';
  orderId: string;
  orderCodigo: string;
  proyectoId: string | null;
  proyectoNombre: string;
  clienteId: string | null;
  clienteNombre: string;
  sucursalId: string | null;
  sucursalNombre: string;
  ciudadEstado: string;
  sustratoPared: string;
  sustratoPiso: string;
  distribucionDrywallPct: number;
  totalLinesPT: number;
  totalUnitsPT: number;
  totalBultos4UP: number;
  bultos4UPSinRecetaEmbalaje?: number;
  totalPuntosPared: number;
  totalPuntosPiso: number;
  materials: MRPExplodedMaterialLine[];
  mueblesConReceta: {
    lineId: string;
    code: string;
    codeEtiqueta: string;
    desc: string;
    cantidad: number;
    insumosCount: number;
    costoTotalBOMUSD: number;
    bomSource: string;
  }[];
  mueblesSinReceta: {
    lineId: string;
    code: string;
    codeEtiqueta: string;
    desc: string;
    cantidad: number;
    familia: string;
  }[];
  serviciosYSuministrosObra?: MRPSubcontractingServiceLine[];
  kpis: {
    totalInsumosUnicos: number;
    capa1Count: number;
    capa2Count: number;
    capa3Count: number;
    cubiertosStockCount: number;
    conDeficitOABCount: number;
    yaReservadosCount: number;
    costoTotalTeoricoUSD: number;
    costoReservarMTOUSD: number;
    costoDeficitOABUSD: number;
    costoComercialCompraOABUSD?: number;
    serviciosYSuministrosCount?: number;
    costoServiciosYSuministrosUSD?: number;
  };
  timestamp: string;
  error?: string;
}



